import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { submitExerciseAnswers } from "../../../api/exercises/questionsService";

// Define question types
type QuestionType = "CHECKBOX" | "RADIO" | "TEXT" | "SLIDER";

interface QuestionOption {
  value: string;
  selected: boolean;
}

interface Question {
  id: string;
  content: string;
  type: QuestionType;
  required: boolean;
  options: string[];
  languageCode: string;
  formattedOptions?: QuestionOption[];
  answer?: string | string[];
}

const QuestionsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const exerciseData = location.state;

  const [questions, setQuestions] = useState<Question[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const questionsPerPage = 5;

  useEffect(() => {
    setIsLoading(true);

    const currentLang = i18n.language || "en";

    if (exerciseData && exerciseData.questions) {
      const processedQuestions = exerciseData.questions
        .flat() // Flatten the array in case it's nested
        .filter((q: Question) => q.languageCode === currentLang)
        .map((q: Question) => {
          let formattedQuestion = { ...q };
          if (q.type === "CHECKBOX") {
            formattedQuestion.formattedOptions = q.options.map(
              (opt: string) => ({
                value: opt,
                selected: false,
              })
            );
            formattedQuestion.answer = [];
          } else if (q.type === "RADIO") {
            formattedQuestion.answer = "";
          } else if (q.type === "TEXT") {
            formattedQuestion.answer = "";
          } else if (q.type === "SLIDER") {
            formattedQuestion.answer =
              q.options && q.options.length > 0 ? q.options[0] : "0";
          }

          return formattedQuestion;
        });

      setQuestions(processedQuestions);
      setErrors({});
    } else {
      const storedData = localStorage.getItem(`exercise_${id}`);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        if (parsedData && parsedData.questions) {
          const processedQuestions = parsedData.questions
            .flat()
            .filter((q: Question) => q.languageCode === currentLang)
            .map((q: Question) => {
              let formattedQuestion = { ...q };

              if (q.type === "CHECKBOX") {
                formattedQuestion.formattedOptions = q.options.map(
                  (opt: string) => ({
                    value: opt,
                    selected: false,
                  })
                );
                formattedQuestion.answer = [];
              } else if (q.type === "RADIO") {
                formattedQuestion.answer = "";
              } else if (q.type === "TEXT") {
                formattedQuestion.answer = "";
              } else if (q.type === "SLIDER") {
                formattedQuestion.answer =
                  q.options && q.options.length > 0 ? q.options[0] : "0";
              }

              return formattedQuestion;
            });

          setQuestions(processedQuestions);
        }
      }
    }
    setCurrentPage(1);
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  }, [exerciseData, id, i18n.language]);

  const handleCheckboxChange = (questionId: string, optionValue: string) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id === questionId) {
          const updatedOptions = (q.formattedOptions || []).map((opt) => {
            if (opt.value === optionValue) {
              return { ...opt, selected: !opt.selected };
            }
            return opt;
          });

          const selectedValues = updatedOptions
            .filter((opt) => opt.selected)
            .map((opt) => opt.value);

          if (selectedValues.length > 0 && errors[questionId]) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated[questionId];
              return updated;
            });
          }

          return {
            ...q,
            formattedOptions: updatedOptions,
            answer: selectedValues,
          };
        }
        return q;
      })
    );
  };

  const handleRadioChange = (questionId: string, value: string) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id === questionId) {
          if (errors[questionId]) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated[questionId];
              return updated;
            });
          }

          return { ...q, answer: value };
        }
        return q;
      })
    );
  };

  const handleTextChange = (questionId: string, value: string) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id === questionId) {
          if (value && errors[questionId]) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated[questionId];
              return updated;
            });
          }

          return { ...q, answer: value };
        }
        return q;
      })
    );
  };

  const handleSliderChange = (questionId: string, value: string) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => {
        if (q.id === questionId) {
          return { ...q, answer: value };
        }
        return q;
      })
    );
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    questions.forEach((q) => {
      if (q.required) {
        if (
          q.type === "CHECKBOX" &&
          (!q.answer || (q.answer as string[]).length === 0)
        ) {
          newErrors[q.id] =
            t("alert.error.required") || "This field is required";
        } else if (q.type === "RADIO" && !q.answer) {
          newErrors[q.id] =
            t("alert.error.required") || "This field is required";
        } else if (
          q.type === "TEXT" &&
          (!q.answer || (q.answer as string).trim() === "")
        ) {
          newErrors[q.id] =
            t("alert.error.required") || "This field is required";
        } else if (q.type === "SLIDER" && !q.answer) {
          newErrors[q.id] =
            t("alert.error.required") || "This field is required";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Calculate pagination
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const indexOfLastQuestion = currentPage * questionsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
  const currentQuestions = questions.slice(
    indexOfFirstQuestion,
    indexOfLastQuestion
  );

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (currentPage < totalPages) {
      goToNextPage();
      return;
    }

    if (!validateForm()) {
      toast.error(
        t("alert.error.pleaseAnswerAllRequiredQuestions") ||
          "Please answer all required questions"
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Format the answers for submission - ensure all answers are arrays
      const formattedAnswers = questions.map((q) => {
        // Explicitly declare the type as string[] to avoid TypeScript errors
        let answerValue: string[] = [];

        // Convert all answer types to arrays
        if (q.type === "CHECKBOX") {
          // Checkbox already returns an array
          answerValue = Array.isArray(q.answer) ? q.answer : [];
        } else if (
          q.type === "RADIO" ||
          q.type === "TEXT" ||
          q.type === "SLIDER"
        ) {
          // For radio, text, and slider, convert single value to array
          answerValue = q.answer ? [q.answer as string] : [];
        }
        // No need for the else case since answerValue is already initialized with []

        return {
          questionId: q.id,
          answer: answerValue,
        };
      });

      // Call the API to submit the answers
      try {
        const planId = exerciseData?.id || "";

        if (!planId) {
          toast.error(t("alert.error.missingPlanId"));
          return;
        }
        await submitExerciseAnswers(id as string, planId, formattedAnswers);
        toast.success(t("alert.success.answersSubmittedSuccessfully"));
        setTimeout(() => {
          navigate("/exercises-list");
        }, 1000);
      } catch (error) {
        toast.error(t("alert.error.failedToSubmitAnswers"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-5xl space-y-5 mt-1 pb-20 lg:pb-4">
        <div className="flex items-center mb-2">
          <button
            className="mr-3 text-primary-600 hover:bg-primary-50 rounded-full p-2 transition"
            onClick={() => navigate("/exercise-list")}
            aria-label="Back"
          >
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-2xl font-bold text-gray-700">
            {exerciseData?.name}
          </h2>
        </div>

        <div
          className="bg-white rounded-xl shadow-md p-6"
          style={{ boxShadow: "6px 8px 24px 4px #F6944D1F" }}
        >
          <div className="space-y-2">
            {isLoading ? (
              <div className="text-center text-gray-400 py-10">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-400 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                <p className="mt-2">{t("login.loading")}</p>
              </div>
            ) : questions.length === 0 ? (
              <div className="text-center text-gray-400 py-10">
                {t("title.noQuestionsAvailable")}
              </div>
            ) : (
              currentQuestions.map((question, index) => (
                <div
                  key={question.id}
                  className="border-b pb-3 last:border-b-0"
                >
                  <div className="flex items-start mb-2">
                    <span className="bg-primary-100 text-primary-700 h-7 w-7 rounded-full flex items-center justify-center mr-2 font-semibold">
                      {indexOfFirstQuestion + index + 1}
                    </span>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-800">
                        {question.content}
                        {question.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </h3>
                    </div>
                  </div>

                  <div className="ml-1 space-y-2">
                    {question.type === "CHECKBOX" && (
                      <div className="space-y-2">
                        {(question.formattedOptions || []).map(
                          (option, optIdx) => (
                            <div
                              key={`${question.id}-${optIdx}`}
                              className="flex items-center"
                            >
                              <input
                                id={`${question.id}-${optIdx}`}
                                type="checkbox"
                                className="h-5 w-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                                checked={option.selected}
                                onChange={() =>
                                  handleCheckboxChange(
                                    question.id,
                                    option.value
                                  )
                                }
                              />
                              <label
                                htmlFor={`${question.id}-${optIdx}`}
                                className="ml-3 text-gray-700"
                              >
                                {option.value}
                              </label>
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {question.type === "RADIO" && (
                      <div className="space-y-2">
                        {question.options.map((option, optIdx) => (
                          <div
                            key={`${question.id}-${optIdx}`}
                            className="flex items-center"
                          >
                            <input
                              id={`${question.id}-${optIdx}`}
                              type="radio"
                              name={`question-${question.id}`}
                              className="h-5 w-5 text-primary-600 border-gray-300 focus:ring-primary-500"
                              checked={question.answer === option}
                              onChange={() =>
                                handleRadioChange(question.id, option)
                              }
                            />
                            <label
                              htmlFor={`${question.id}-${optIdx}`}
                              className="ml-3 text-gray-700"
                            >
                              {option}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}

                    {question.type === "TEXT" && (
                      <div>
                        <textarea
                          id={`question-${question.id}`}
                          rows={4}
                          className={`w-full px-3 py-2 border ${
                            errors[question.id]
                              ? "border-red-500"
                              : "border-gray-300"
                          } rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                          placeholder={t("input.placeholder.enterYourAnswer")}
                          value={question.answer as string}
                          onChange={(e) =>
                            handleTextChange(question.id, e.target.value)
                          }
                        />
                      </div>
                    )}

                    {question.type === "SLIDER" && (
                      <div>
                        <div className="relative mb-1">
                          <input
                            type="range"
                            min={question.options[0]}
                            max={question.options[1]}
                            value={question.answer as string}
                            onChange={(e) =>
                              handleSliderChange(question.id, e.target.value)
                            }
                            className="w-full accent-primary-500 mt-4"
                          />
                          <div className="absolute w-full flex justify-center -top-3">
                            <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-md text-sm font-medium">
                              {question.answer}
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between text-xs mt-1 text-primary-500">
                          {question.options.map((option, index) => (
                            <span key={index}>{option}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {errors[question.id] && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors[question.id]}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination controls - only show when not loading */}
          {!isLoading && totalPages > 1 && (
            <div className="mt-2 flex justify-center items-center space-x-2">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`p-2 rounded-full ${currentPage === 1 ? "text-gray-400 cursor-not-allowed" : "text-primary-600 hover:bg-primary-50"}`}
                aria-label="Previous page"
              >
                <ChevronLeft size={20} />
              </button>

              {/* Numbered pagination */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (pageNumber) => (
                  <button
                    key={pageNumber}
                    onClick={() => {
                      setCurrentPage(pageNumber);
                      window.scrollTo(0, 0);
                    }}
                    className={`w-8 h-8 flex items-center justify-center rounded-full ${
                      currentPage === pageNumber
                        ? "bg-primary-600 text-white"
                        : "text-gray-700 hover:bg-primary-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                )
              )}

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-full ${currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : "text-primary-600 hover:bg-primary-50"}`}
                aria-label="Next page"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}

          {/* Only show action buttons when not loading */}
          {!isLoading && (
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => navigate("/exercise-list")}
                className="mr-4 px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-primary-300"
              >
                {t("button.cancel")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-primary-300 flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="mr-2 h-4 w-4 border-1 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t("login.loading")}
                  </>
                ) : currentPage === totalPages ? (
                  t("button.submit")
                ) : (
                  t("button.next")
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionsPage;
